// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { MyAccountPageContext } from "@insite/content-library/Pages/MyAccountPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadCurrentAccountSummery from "@rugby/App/Handlers/Accounts/LoadCurrentAccountSummery";
import { xAccountSummeryState } from "@rugby/App/Store/Reducers";
import { getCurrentAccountSummery } from "@rugby/App/Store/Selectors/AccountSummerySelectors";
import { MediaQuery } from "@rugby/XDL/Utilities/MediaQuery";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState & xAccountSummeryState) => ({
    session: state.context.session,
    accountSummery: getCurrentAccountSummery(state),
});

const mapDispatchToProps = {
    loadCurrentAccountSummery,
};

export interface AccountSummeryStyles {
    container?: InjectableCss;
    quickLinksGridItem?: GridItemProps;
    mainSectionGridItem?: GridItemProps;
    mainSectionGridContainer?: GridContainerProps;
    accountMessagesGridItem?: GridItemProps;
    dashboardWidgetsGridItem?: GridItemProps;
    tableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: InjectableCss;
    dataCellfirst?: InjectableCss;
    dataCellAll?: DataTableHeaderProps;
    titleText?: TypographyPresentationProps;
    headerContainer?: InjectableCss;
    pdfLink?: LinkPresentationProps;
}

export const accountSummeryStyles: AccountSummeryStyles = {
    quickLinksGridItem: { width: [12, 12, 12, 2, 2] },
    mainSectionGridItem: { width: [12, 12, 12, 10, 10] },
    accountMessagesGridItem: { width: 12 },
    dashboardWidgetsGridItem: { width: 12 },
    container: {
        css: css`
            padding: 0;
            width: 100%;
            margin-top: 25px;
            margin-bottom: 50px;
        `,
    },
    titleText: {
        variant: "h4",
    },
    headerContainer: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: space-between;

            ${MediaQuery.lte("sm")} {
                flex-flow: column;
            }
        `,
    },
    pdfLink: {
        css: css`
            margin-top: 5px;

            ${MediaQuery.lte("sm")} {
                margin-bottom: 15px;
            }
        `,
    },
    tableWrapper: {
        css: css`
            overflow: auto;
        `,
    },
    dataTableHeaders: {
        css: css`
            text-align: center;
        `,
    },
    dataCell: {
        css: css`
            text-align: center;
        `,
    },
    dataCellAll: {
        css: css`
            text-align: left;
        `,
    },
    dataCellfirst: {
        css: css`
            text-align: left;
        `,
    },
};

const styles = accountSummeryStyles;

const AccountSummeryTable: React.FC<Props> = (props: Props) => {
    if (props.session?.userRoles?.indexOf("CanViewAr") === -1) {
        return null;
    }

    useEffect(() => {
        if (!props.accountSummery) {
            props.loadCurrentAccountSummery();
        }
    }, []);

    const pastStatements = siteMessage("RUGBY_AccountSummery_PastStatements");

    return (
        <StyledWrapper data-test-selector="accountSummery_container">
            {props.accountSummery && (
                <StyledWrapper {...styles.container}>
                    <StyledWrapper {...styles.headerContainer}>
                        <Typography {...styles.titleText} data-test-selector="accountSummery_tableTitle">
                            {translate("Account Summary")}
                        </Typography>
                        {props.accountSummery.pastStatementsPdfLink && (
                            <Link {...styles.pdfLink} target="_blank" href={props.accountSummery.pastStatementsPdfLink}>
                                <Button>{pastStatements}</Button>
                            </Link>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.tableWrapper}>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Amount")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Count")}</DataTableHeader>
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                <DataTableRow {...styles.dataTableRow} data-test-selector="accountSummery_current">
                                    <DataTableCell {...styles.dataCellfirst}>{translate("Current")}</DataTableCell>
                                    <DataTableCell
                                        {...styles.dataCell}
                                        data-test-selector="accountSummery_currentBalance"
                                    >
                                        {props.accountSummery.currentBalance}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataCell}
                                        data-test-selector="accountSummery_currentBalanceCount"
                                    >
                                        {props.accountSummery.currentBalanceCount}
                                    </DataTableCell>
                                </DataTableRow>
                                <DataTableRow
                                    {...styles.dataTableRow}
                                    data-test-selector="accountSummery_pastDueBalance1"
                                >
                                    <DataTableCell
                                        {...styles.dataCellfirst}
                                        data-test-selector="accountSummery_pastDueBalance1Label"
                                    >
                                        {props.accountSummery.pastDueBalance1Label}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataCell}
                                        data-test-selector="accountSummery_pastDueBalance1Value"
                                    >
                                        {props.accountSummery.pastDueBalance1}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataCell}
                                        data-test-selector="accountSummery_pastDueBalance1Count"
                                    >
                                        {props.accountSummery.pastDueBalance1Count}
                                    </DataTableCell>
                                </DataTableRow>
                                <DataTableRow
                                    {...styles.dataTableRow}
                                    data-test-selector="accountSummery_pastDueBalance2"
                                >
                                    <DataTableCell
                                        {...styles.dataCellfirst}
                                        data-test-selector="accountSummery_pastDueBalance2Label"
                                    >
                                        {props.accountSummery.pastDueBalance2Label}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataCell}
                                        data-test-selector="accountSummery_pastDueBalance2Value"
                                    >
                                        {props.accountSummery.pastDueBalance2}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataCell}
                                        data-test-selector="accountSummery_pastDueBalance2Count"
                                    >
                                        {props.accountSummery.pastDueBalance2Count}
                                    </DataTableCell>
                                </DataTableRow>
                                <DataTableRow
                                    {...styles.dataTableRow}
                                    data-test-selector="accountSummery_pastDueBalance3"
                                >
                                    <DataTableCell
                                        {...styles.dataCellfirst}
                                        data-test-selector="accountSummery_pastDueBalance3Label"
                                    >
                                        {props.accountSummery.pastDueBalance3Label}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataCell}
                                        data-test-selector="accountSummery_pastDueBalance3Value"
                                    >
                                        {props.accountSummery.pastDueBalance3}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataCell}
                                        data-test-selector="accountSummery_pastDueBalance3Count"
                                    >
                                        {props.accountSummery.pastDueBalance3Count}
                                    </DataTableCell>
                                </DataTableRow>
                                <DataTableRow
                                    {...styles.dataTableRow}
                                    data-test-selector="accountSummery_pastDueBalance4"
                                >
                                    <DataTableCell
                                        {...styles.dataCellfirst}
                                        data-test-selector="accountSummery_pastDueBalance4Label"
                                    >
                                        {props.accountSummery.pastDueBalance4Label}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataCell}
                                        data-test-selector="accountSummery_pastDueBalance4Value"
                                    >
                                        {props.accountSummery.pastDueBalance4}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataCell}
                                        data-test-selector="accountSummery_pastDueBalance4Count"
                                    >
                                        {props.accountSummery.pastDueBalance4Count}
                                    </DataTableCell>
                                </DataTableRow>
                                <DataTableRow
                                    {...styles.dataTableRow}
                                    data-test-selector="accountSummery_futureBalance"
                                >
                                    <DataTableCell
                                        {...styles.dataCellfirst}
                                        data-test-selector="accountSummery_futureBalance_label"
                                    >
                                        {translate("Future")}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataCell}
                                        data-test-selector="accountSummery_futureBalance_value"
                                    >
                                        {props.accountSummery.futureBalance}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.dataCell}
                                        data-test-selector="accountSummery_futureBalance_count"
                                    >
                                        {props.accountSummery.futureBalanceCount}
                                    </DataTableCell>
                                </DataTableRow>
                            </DataTableBody>
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader {...styles.dataCellAll}>{translate("All")}</DataTableHeader>
                                <DataTableHeader
                                    {...styles.dataTableHeaders}
                                    data-test-selector="accountSummery_accountBalance"
                                >
                                    {props.accountSummery.accountBalance}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.dataTableHeaders}
                                    data-test-selector="accountSummery_totalCount"
                                >
                                    {props.accountSummery.totalCount}
                                </DataTableHeader>
                            </DataTableHead>
                        </DataTable>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const accountSummeryWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSummeryTable),
    definition: {
        allowedContexts: [MyAccountPageContext],
        group: "Xngage Custom" as any,
        displayName: "Account Summery Table",
    },
};

export default accountSummeryWidget;
